import React from 'react';
import Modal from './Modal';
import Button from './Button';
import { colors, spacing } from '../../styles/designTokens';

/**
 * ConfirmDialog Component
 * 
 * Confirmation dialog for destructive or important actions.
 * Uses Modal component with standardized confirmation UI.
 * 
 * Usage:
 * <ConfirmDialog
 *   isOpen={showConfirm}
 *   onClose={() => setShowConfirm(false)}
 *   onConfirm={handleDelete}
 *   title="Delete User?"
 *   message="This will permanently delete the user and all their data. This action cannot be undone."
 *   confirmLabel="Delete"
 *   confirmVariant="danger"
 * />
 * 
 * Props:
 * - isOpen: Boolean - controls visibility
 * - onClose: Function - called when dialog should close
 * - onConfirm: Function - called when user confirms
 * - title: String - dialog title (required)
 * - message: String - confirmation message (required)
 * - confirmLabel: String - confirm button label (default: 'Confirm')
 * - cancelLabel: String - cancel button label (default: 'Cancel')
 * - confirmVariant: 'primary' | 'danger' (default: 'primary')
 * - isLoading: Boolean - shows loading state on confirm button
 * - icon: React node - optional icon to display
 */

const ConfirmDialog = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  confirmVariant = 'primary',
  isLoading = false,
  icon
}) => {
  const handleConfirm = async () => {
    await onConfirm();
    // Note: Parent component should handle closing after async operation
  };

  // Icon variants for common confirm types
  const defaultIcons = {
    danger: (
      <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
        />
      </svg>
    ),
    primary: (
      <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    )
  };

  const displayIcon = icon || defaultIcons[confirmVariant] || defaultIcons.primary;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="sm"
      closeOnClickOutside={!isLoading}
      closeOnEscape={!isLoading}
      showCloseButton={!isLoading}
    >
      {/* Icon */}
      {displayIcon && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            marginBottom: spacing.md,
            color: confirmVariant === 'danger' ? colors.error.main : colors.primary.main
          }}
          aria-hidden="true"
        >
          {displayIcon}
        </div>
      )}

      {/* Message */}
      <p
        style={{
          color: colors.neutral[700],
          textAlign: 'center',
          lineHeight: 1.6,
          marginBottom: spacing.lg
        }}
      >
        {message}
      </p>

      {/* Actions */}
      <div
        style={{
          display: 'flex',
          gap: spacing.md,
          justifyContent: 'flex-end'
        }}
      >
        <Button
          variant="outline"
          size="md"
          onClick={onClose}
          disabled={isLoading}
        >
          {cancelLabel}
        </Button>

        <Button
          variant={confirmVariant}
          size="md"
          onClick={handleConfirm}
          loading={isLoading}
        >
          {confirmLabel}
        </Button>
      </div>
    </Modal>
  );
};

/**
 * useConfirmDialog Hook
 * 
 * Convenient hook for managing confirm dialog state
 * 
 * Usage:
 * const { show, ConfirmDialog } = useConfirmDialog({
 *   title: 'Delete User?',
 *   message: 'This action cannot be undone.',
 *   onConfirm: handleDelete,
 *   confirmVariant: 'danger'
 * });
 * 
 * // In component:
 * <button onClick={() => show()}>Delete</button>
 * {ConfirmDialog}
 */
export const useConfirmDialog = ({
  title,
  message,
  onConfirm,
  confirmLabel,
  cancelLabel,
  confirmVariant,
  icon
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);

  const show = () => setIsOpen(true);
  const hide = () => setIsOpen(false);

  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      await onConfirm();
      hide();
    } catch (error) {
      // Error handling should be done in onConfirm
      console.error('Confirm action failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const dialog = (
    <ConfirmDialog
      isOpen={isOpen}
      onClose={hide}
      onConfirm={handleConfirm}
      title={title}
      message={message}
      confirmLabel={confirmLabel}
      cancelLabel={cancelLabel}
      confirmVariant={confirmVariant}
      isLoading={isLoading}
      icon={icon}
    />
  );

  return {
    show,
    hide,
    isOpen,
    ConfirmDialog: dialog
  };
};

export default ConfirmDialog;

